/*
 *  Copyright (c) Qwoach LLC 2022. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 29/7/2022
 */

import SessionPackageWrapper from "@shared/CoachingPackage/SessionPackageWrapper";
import {Type} from "@qwoach/class-transformer-wrapper"
import FeeItem from "@shared/CoachingPackage/FeeItem";
import SendingReminderWrapper from "@shared/scheduler/reminders/SendingReminderWrapper";
import InvoiceableDbObject from "@shared/InvoiceableDbObject";
import {ObjectTypeEnum} from "@shared/Invoice/ObjectTypeEnum";
import {MessagePlaceholdersEnum, noBrackets} from "@/core/components/template/PlaceholdersData";
import {FilterableProperty} from "@shared/Filterable/PropertyDecorators";
import {classToClass} from "@qwoach/class-transformer-wrapper";
import PartialPaymentsSettings from "@shared/CoachingPackage/PartialPaymentsSettings";
import {CoachingPackageType} from "@shared/CoachingPackage/CoachingPackageType";
import GroupCoachingSettings from "@shared/CoachingPackage/GroupCoachingSettings.js";
import DripContentPackageWrapper from "@shared/CoachingPackage/DripContentPackageWrapper";

export default class CoachingPackageTemplate extends InvoiceableDbObject {


	coachId: string = null
	coachName: string = null

	packageType: CoachingPackageType = CoachingPackageType.ONE_ON_ONE

	@FilterableProperty()
	title: string = null
	description: string = null

	isSessionLimitEnabled: boolean = false
	bookableSessionsLimit: number = 1

	@Type(() => SessionPackageWrapper)
	sessionPackageWrapper: SessionPackageWrapper[] = []

	@Type(() => DripContentPackageWrapper)
	dripContentWrapper: DripContentPackageWrapper[] = [new DripContentPackageWrapper()]

	@Type(() => FeeItem)
	feesAndDiscounts: FeeItem[] = []

	@Type(() => SendingReminderWrapper)
	welcomeMaterials: SendingReminderWrapper = new SendingReminderWrapper()

	@Type(() => PartialPaymentsSettings)
	partialPaymentsSettings: PartialPaymentsSettings = new PartialPaymentsSettings()

	@Type(() => GroupCoachingSettings)
	groupSettings: GroupCoachingSettings = null

	netRevenue: number = 0
	soldCount: number = 0

	validTillMs: number = -1

	constructor(packageType) {
		super();
		this.packageType = packageType || CoachingPackageType.ONE_ON_ONE
	}

	getSignedLegalDocsCount() {
		// let legalDocs = this.welcomeMaterials.getLegalDocsFileRefs()
		// return legalDocs.filter(doc => doc.)
		// return this.sessionPackageWrapper.reduce((prev, cur) => prev + cur.scheduledSessionRefs.length, 0)
		return 0
	}
	totalScheduledSessionsCount() {
		return this.sessionPackageWrapper.reduce((prev, cur) => prev + cur.scheduledSessionRefs.length, 0)
	}
	totalConfirmedSessionsCount() {
		return this.sessionPackageWrapper.reduce((prev, cur) => prev + cur.getConfirmedSessionCount(), 0)
	}
	totalSessionsCount() {
		return this.sessionPackageWrapper.reduce((prev, cur) => prev + cur.sessionsCount, 0)
	}
	totalPrice() {
		return Math.max(0, this.totalSessionPrice() - this.totalDiscountPrice() + this.totalFeesPrice())
	}
	totalPriceNoDiscounts() {
		return Math.max(0, this.totalSessionPrice() + this.totalFeesPrice())
	}
	totalSessionPrice() {
		return this.sessionPackageWrapper.reduce((prev, cur) => prev + cur.sessionPrice * cur.sessionsCount, 0)
	}
	totalFeesPrice() {
		return this.feesAndDiscounts.filter(item => !item.isDiscount).reduce((prev, cur) => prev + cur.amount, 0)
	}
	totalDiscountPrice() {
		return this.feesAndDiscounts.filter(item => item.isDiscount).reduce((prev, cur) => prev + cur.amount, 0)
	}

	getInvoiceObjectType(){
		return ObjectTypeEnum.COACHING_PACKAGE_TEMPLATE
	}

	getInvoiceObjectTitle(){
		return this.title
	}

	getInvoiceBillingData(){
		let billingData = []

		let totalSessionsCount = this.totalSessionsCount()
		let totalSessionPrice = this.totalSessionPrice()

		if(totalSessionPrice > 0){
			billingData.push({title: `${totalSessionsCount}x sessions`, price: totalSessionPrice})
		}

		for(let feeItem of this.feesAndDiscounts){
			let amount = feeItem.getAmount()
			billingData.push({title: feeItem.description, price: amount})
		}

		return billingData
	}

	createOneTimePaymentSettings(){
		let paymentSettings = new PartialPaymentsSettings()
		paymentSettings.installmentsTotal = 1
		paymentSettings.splitEqually(this.totalPrice())
		return paymentSettings
	}

	createAllPaymentSettingsArray(){
		let oneTimePaymentSettings = this.createOneTimePaymentSettings()
		let oneTimePaymentSettings2 = this.createOneTimePaymentSettings()
		let oneTimePaymentSettings3 = this.createOneTimePaymentSettings()
		let isPartialEnabled = this.partialPaymentsSettings && this.partialPaymentsSettings.isEnabled
		let partialSettingsArray = isPartialEnabled ? [this.partialPaymentsSettings] : []
		let partialSettingsArray2 = classToClass(partialSettingsArray[0])
		let partialSettingsArray3 = classToClass(partialSettingsArray[0])
		// let array = [new PartialPaymentsSettings(), new PartialPaymentsSettings(), ...partialSettingsArray, partialSettingsArray2, partialSettingsArray3, oneTimePaymentSettings, oneTimePaymentSettings2, oneTimePaymentSettings3]
		let array = [oneTimePaymentSettings, ...partialSettingsArray]

		let maxSum = 0
		array.forEach(item => maxSum = Math.max(maxSum, item.totalPaymentAmount()))
		array.forEach(item => item.savingsAmount = Math.max(item.savingsAmount, maxSum - item.totalPaymentAmount()))

		return array

	}

	recalculatePartialPayments(){
		let price = this.totalPrice()
		// let price = this.partialPaymentsSettings.discardDiscounts ? this.totalPriceNoDiscounts() : this.totalPrice()
		this.partialPaymentsSettings.splitEqually(price)
	}

	isOneOnOnePackage(){
		return this.packageType === CoachingPackageType.ONE_ON_ONE
	}

	isGroupPackage(){
		return this.packageType === CoachingPackageType.GROUP
	}

	setIsOneOnOnePackage(){
		this.packageType = CoachingPackageType.ONE_ON_ONE
	}

	setIsGroupPackage(){
		this.packageType = CoachingPackageType.GROUP
	}

	applyGroupSettings(groupSettings){
		this.groupSettings = groupSettings
		for(let i = 0; i < groupSettings.numberOfSessions.length; ++i){
			this.sessionPackageWrapper[i].sessionsCount = groupSettings.numberOfSessions[i]
		}
		if(this.groupSettings.groupTitle){
			this.title = this.groupSettings.groupTitle
		}
	}
	addSessionsFromGroupSettings(groupSettings){
		let wrapper = this.findSessionWrapperBySessionId(groupSettings.sessionTypeId)
		if(!wrapper || wrapper.sessionDuration !== groupSettings.sessionDuration){
			wrapper = new SessionPackageWrapper()
			this.sessionPackageWrapper.push(wrapper)
		}
		wrapper.sessionTypeId = groupSettings.sessionTypeId
		wrapper.sessionTypeTitle = groupSettings.sessionTypeTitle
		wrapper.sessionDuration = groupSettings.sessionDuration
		wrapper.sessionsCount += groupSettings.numberOfSessions

		return wrapper
	}

	findSessionWrapperBySessionId(sessionId){
		for(let wrapper of this.sessionPackageWrapper){
			if(wrapper.sessionTypeId === sessionId){
				return wrapper
			}
		}
		return null
	}


	getScheduledSessionsRefsFlat(){
		let refs = []
		for(let wrapper of this.sessionPackageWrapper){
			for(let ref of wrapper.scheduledSessionRefs){
				refs.push(ref)
			}
		}
		return refs
	}

	createWelcomeEmailData(clientId){

		if(!this.welcomeMaterials.initialReminder){
			return {}
		}

		let emailReminder = this.welcomeMaterials//.initialReminder

		return this.getReminderEmailData(clientId, emailReminder)

	}


	getReminderEmailData(clientId, dripReminderWrapper){

		let emailReminder = dripReminderWrapper.initialReminder

		let legalDocsRefs = dripReminderWrapper.getLegalDocsFileRefs()
		let attachedFilesRefs = dripReminderWrapper.getNonLegalDocsFileRefs()

		let legalDocsNameUrlRefs = legalDocsRefs.map(ref => ref.toNameAndLink(this.coachId, clientId))
		let attachedFilesNameUrlRefs = attachedFilesRefs.map(ref => ref.toNameAndLink(this.coachId, clientId))

		let template = {
			subject: emailReminder.subject,
			body: emailReminder.body,
		}

		let legalDocsName = noBrackets(MessagePlaceholdersEnum.LEGAL_DOCS)
		let attachedFilesName = noBrackets(MessagePlaceholdersEnum.ATTACHED_FILES)

		let data = {
			coachId: this.coachId,
			clientId: clientId,
			[legalDocsName]: legalDocsNameUrlRefs,
			[attachedFilesName]: attachedFilesNameUrlRefs
		}

		return {template, data}
	}

	getLegalDocsRefs(clientId){
		return this.welcomeMaterials.getLegalDocsFileRefs()
	}

	getSessionWrapperBySessionIndex(index){
		let sessionCount = 0
		for(let i = 0; i < this.sessionPackageWrapper.length; ++i){
			let sessionWrapper = this.sessionPackageWrapper[i]
			sessionCount += sessionWrapper.sessionsCount
			if(sessionCount >= index){
				return sessionWrapper
			}
		}
		return null
	}

	findSessionWrapper(sessionPackageWrapperId){
		let sessionWrapper = null
		if(sessionPackageWrapperId){
			let index = this.sessionPackageWrapper.findIndex(wrapper => wrapper.id === sessionPackageWrapperId)
			if(index >= 0){
				sessionWrapper = this.sessionPackageWrapper[index]
			}
		}

		// if(!sessionWrapper){
		// 	let index = this.sessionPackageWrapper.findIndex(wrapper => wrapper.sessionTypeId === eventType.id)
		// 	if(index >= 0){
		// 		sessionWrapper = this.sessionPackageWrapper[index]
		// 	}
		// }
		return sessionWrapper
	}

	findScheduledSessionRef(sessionId, sessionPackageWrapperId){
		let sessionWrapper = this.findSessionWrapper(sessionPackageWrapperId)
		if(!sessionWrapper){
			return
		}
		return sessionWrapper.findSessionRefById(sessionId)
	}

	overrideEventType(eventType, sessionPackageWrapperId){
		let sessionWrapper = this.findSessionWrapper(sessionPackageWrapperId)

		if(!sessionWrapper){
			console.warn("CoachingPackageTemplate.overrideEventType: can't find sessionWrapper with provided parameters = ", ...arguments)
			return eventType
		}

		console.log("CoachingPackageTemplate.overrideEventType: overriding eventType with values from sessionWrapper = ", sessionWrapper)
		eventType.duration = sessionWrapper.sessionDuration

		//this session is a part of a package which was already purchased. So we don;t ask for payment at this point.
		eventType.price = 0
		eventType.paidSession = false

		eventType.sendingReminderWrapper = sessionWrapper.sendingReminderWrapperArray
		// eventType.price = sessionWrapper.sessionPrice
		// eventType.paidSession = sessionWrapper.sessionPrice > 0

		//for coaching sessions we ignore time upper limit so clients can schedule all future sessions
		eventType.rollingDays = 0

		return eventType
	}


	createCopy(newName){
		let templateCopy = classToClass(this)
		templateCopy.regenerateId()
		templateCopy.setCreatedAtNow()
		templateCopy.title = newName

		templateCopy.netRevenue = 0
		templateCopy.soldCount = 0

		return templateCopy

	}

	getLatestSessionRefByTime(){
		let latestRef
		for(let packageWrapper of this.sessionPackageWrapper){
			let latestRefLocal = packageWrapper.getLatestSessionRefByTime
			if(!latestRef || latestRefLocal.endTime() > latestRef.endTime()){
				latestRef = latestRefLocal
			}
		}
		return latestRef
	}

	isExpired(){
		let timeNow = new Date().getTime()
		return this.validTillMs > 0 && this.validTillMs < timeNow
	}

}
